import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Settings } from '../settings-data/settings.model';
import { TrackedTimeFiltersEnum } from './tracked-time-data.data';
import { TrackedTimeDataStore } from './tracked-time-data.store';
import { TrackedTimeDataService } from './tracked-time-data.service';
import { TrackedTimeDataBuilder } from './tracked-time-data.builder';
import { EntityDataEffects } from '../entity-data/entity-data.effects';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { concat, Observable } from 'rxjs';
import {
  filter,
  flatMap,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { DialogService } from '../dialog/dialog.service';
import {
  ADD_CLIENT_QUERY_PARAM,
  ADD_TRACKED_TIME_QUERY_PARAM,
  DOCUMENTS_NEW_ROUTE,
} from '../document-data/document-data.data';
import {
  ActionsEnum as EntityActionsEnum,
  ClearList,
  clearList,
  DeleteEntityNetworkSuccess,
  SaveEntityNetworkSuccess,
  setFiltersFetched,
  SetFiltersFetched,
  SetListFetched,
  setListFetched,
} from '../entity-data/entity-data.actions';
import { BannerCanvasTypesEnum } from '../features-data/features-data.data';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { INVOICES_MASTER_ROUTE } from '../main/main.data';
import { NavigationService } from '../navigation/navigation.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { TrackedTime } from './models/tracked-time.model';
import {
  ActionsEnum,
  BillTrackedTime,
  clearDraftTrackedTime,
  ClearDraftTrackedTime,
  setDraftTrackedTime,
  SetDraftTrackedTime,
  setDraftTrackedTimeError,
  SetDraftTrackedTimeError,
} from './tracked-time-data.actions';
import { IState } from './tracked-time-data.reducer';
import { EntityTypesEnum } from '../../app.enum';
import { AppStore } from '../../store';

@Injectable()
export class TrackedTimeDataEffects extends EntityDataEffects<
  IState,
  TrackedTime
> {
  constructor(
    private _trackedTimeDataStore: TrackedTimeDataStore,
    trackedTimeDataBuilder: TrackedTimeDataBuilder,
    trackedTimeDataService: TrackedTimeDataService,
    actions: Actions,
    appStore: AppStore,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      actions,
      appStore,
      trackedTimeDataService,
      _trackedTimeDataStore,
      trackedTimeDataBuilder,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );
  }

  @Effect() loadSearchParams = this.loadSearchParams;
  @Effect() persistSearchParams = this.persistSearchParams;
  @Effect() fetchList = this.fetchList;
  @Effect() searchList = this.searchList;
  @Effect() fetchNextPage = this.fetchNextPage;
  @Effect() fetchFilters = this.fetchFilters;
  @Effect() triggerRefreshFiltersWithDelay = this
    .triggerRefreshFiltersWithDelay;
  @Effect() getEntityById = this.getEntityById;
  @Effect() setEditingEntityContent = this.setEditingEntityContent;
  @Effect() refreshEditingEntity = this.refreshEditingEntity;
  @Effect() setEditingEntityProperties = this.setEditingEntityProperties;
  @Effect() setEditingEntityField = this.setEditingEntityField;
  @Effect() saveNewEntity = this.saveNewEntity;
  @Effect() saveNewEntityError = this.saveNewEntityError;
  @Effect() updateEntity = this.updateEntity;
  @Effect() saveEditingEntity = this.saveEditingEntity;
  @Effect() saveExistingEntityError = this.saveExistingEntityError;
  @Effect() saveEntityStore = this.saveEntityStore;
  @Effect() deleteEntityStore = this.deleteEntityStore;
  @Effect() saveEntityToNetwork = this.saveEntityToNetwork;
  @Effect() deleteEntityNetwork = this.deleteEntityNetwork;

  /**
   * Create a new tracked time
   */
  @Effect()
  createNewTrackedTime: Observable<
    SetDraftTrackedTimeError | ClearDraftTrackedTime | SetDraftTrackedTime
  > = this.actions.pipe(
    ofType(ActionsEnum.NEW_DRAFT_TRACKED_TIME),
    switchMap(() => {
      return this._trackedTimeDataStore.getDraftTrackedTime().pipe(
        take(1),
        withLatestFrom(
          this.settingsDataStore.getSettings(),
          this.featuresDataStore.featureToggles$
        ),
        map(
          ([trackedTime, settings, toggles]: [
            TrackedTime,
            Settings,
            Toggles
          ]) => {
            const featureName = Toggles.KEYS.TIME_TRACKING;

            if (trackedTime && trackedTime.startDate) {
              const message = this.translateService.instant(
                'TRACKED_TIME.ERROR.NEW_TRACKED_TIME_ACTIVE'
              );

              return setDraftTrackedTimeError(
                this.entityType,
                new Error(message)
              );
            } else if (
              toggles.isNoAccess(featureName) ||
              toggles.isReadOnlyAccess(featureName)
            ) {
              this.dialogService.openGrowthDialog(
                BannerCanvasTypesEnum.feature_lock,
                featureName
              );

              return clearDraftTrackedTime(this.entityType);
            } else {
              const newTrackedTime = this.entityDataBuilder.buildEntity(
                {},
                settings,
                false,
                true
              );

              return setDraftTrackedTime(this.entityType, newTrackedTime);
            }
          }
        )
      );
    })
  );

  /**
   * Bill tracked time
   */
  @Effect()
  billTrackedTime: Observable<
    ClearList | SetFiltersFetched
  > = this.actions.pipe(
    ofType(ActionsEnum.BILL_TRACKED_TIME),
    flatMap(({ payload }: BillTrackedTime) => {
      const { trackedTimeId, clientReferenceId } = payload;

      const queryParams = {
        [ADD_CLIENT_QUERY_PARAM]: clientReferenceId,
        [ADD_TRACKED_TIME_QUERY_PARAM]: trackedTimeId,
      };

      this.navigationService.goTo(
        [INVOICES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
        { queryParams }
      );

      // TODO [refactor]: once documents are refactored, clear lists & filters after invoice has been saved/deleted
      return concat([
        clearList(this.entityType, TrackedTimeFiltersEnum.unbilled),
        clearList(this.entityType, TrackedTimeFiltersEnum.billed),
        setFiltersFetched(
          this.entityType,
          false,
          TrackedTimeFiltersEnum.unbilled
        ),
        setFiltersFetched(
          this.entityType,
          false,
          TrackedTimeFiltersEnum.billed
        ),
      ]);
    })
  );

  @Effect()
  setListsAsUnfetchedAfterClientUpdates: Observable<SetListFetched> = this.actions.pipe(
    ofType(
      EntityActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS,
      EntityActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS
    ),
    filter(
      ({ payload }: SaveEntityNetworkSuccess | DeleteEntityNetworkSuccess) => {
        return payload.entityType === EntityTypesEnum.client;
      }
    ),
    switchMap(() => {
      return concat([
        setListFetched(this.entityType, false, TrackedTimeFiltersEnum.unbilled),
        setListFetched(this.entityType, false, TrackedTimeFiltersEnum.billed),
      ]);
    })
  );
}
